<script lang="ts" setup>
import { Button } from '~ds/components/ui/button';

import type { AuthAccountSwitchedMessage } from '~/constants/auth';

import {
  AUTH_ACCOUNT_SWITCHED_HERE_STATE,
  AUTH_ACCOUNT_SWITCHED_MESSAGE,
  AUTH_BROADCAST_CHANNEL,
  AUTH_LOGGED_OUT_MESSAGE,
} from '~/constants/auth';

const { logout, isLoggedIn } = useAuth();
const { $accountSwitchFollower } = useNuxtApp();
const route = useRoute();

const openModal = ref<boolean>(false);
const switchedHere = useState<boolean>(AUTH_ACCOUNT_SWITCHED_HERE_STATE, () => false);

let channel: BroadcastChannel | undefined;

const isAccountSwitch = (data: unknown): data is AuthAccountSwitchedMessage =>
  typeof data === 'object' && data !== null && (data as Partial<AuthAccountSwitchedMessage>).type === AUTH_ACCOUNT_SWITCHED_MESSAGE;

const isUnder = (path: string, section: string) => path === section || path.startsWith(`${section}/`);

// A tab already in the new account's part of the site reloads where it is; one showing the other role's pages
// would be turned away from them, so it follows the switching tab to its landing page.
const followAccountSwitch = (landingPath: string) => {
  if (isUnder(clusterRelativePath(route.path), landingPath)) {
    reloadNuxtApp({ force: true });
    return;
  }

  void navigateTo(clusterUrl(landingPath), { replace: true, external: true });
};

onMounted(() => {
  if (typeof BroadcastChannel === 'undefined') {
    return;
  }

  channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
  channel.onmessage = (event) => {
    // isLoggedIn guards tabs that already know they're logged out (e.g. sitting
    // on the login page) from showing the modal when yet another tab logs out.
    if (event.data === AUTH_LOGGED_OUT_MESSAGE && isLoggedIn.value) {
      openModal.value = true;
    }

    // A switch in another tab moved the shared session to another account; this tab still shows the old one.
    if (isAccountSwitch(event.data)) {
      if (isLoggedIn.value && !switchedHere.value) {
        followAccountSwitch(event.data.path);
        return;
      }
      $accountSwitchFollower.release();
    }
  };
});

onUnmounted(() => {
  channel?.close();
  channel = undefined;
});

const relogin = () => {
  void logout();
};
</script>

<template>
  <KUICoreDialog
    v-model="openModal"
    has-overlay
    prevent-close
    :show-close-button="false"
    content-width="fit"
  >
    <div data-testid="session-unauthed-modal">
      <p class="my-5">
        {{ $t('general.sessionTimeout.unauthed') }}
      </p>
      <div class="flex justify-end">
        <Button
          data-testid="relogin-button"
          size="sm"
          type="button"
          @click="relogin()"
        >
          {{ $t('general.sessionTimeout.relogin') }}
        </Button>
      </div>
    </div>
  </KUICoreDialog>
</template>
